import CreatableSelect from "@/Components/Select/CreatableSelect";
import { useSetModal } from "@/shared/atoms/useModal";
import useAreaHook from "@/shared/hooks/useRegionHook";
import useZipCodesHook from "@/shared/hooks/useZipCodesHook";
import useZipcodesByRadius from "@/shared/queries/zipcode/useZipcodesByRadius";
import { increments } from "@/shared/utils/constant";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Icon from "../Icon/Icon";
import InputColor from "../Input/InputColor";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import Select from "../Select/Select";
import Spinner from "../Spinner/Spinner";
import useCountryConfigHook from "@/shared/hooks/useCountryConfigHook";

export interface FindMyZipcodesProps extends ModalProps {}

const FindMyZipcodes = ({ ...props }: FindMyZipcodesProps) => {
  const { t } = useTranslation("", { keyPrefix: "zip_codes" });
  const setModal = useSetModal();
  const [radius, setRadius] = useState(1000);
  const query = useZipcodesByRadius(radius);
  const [selected, setSelected] = useState("");
  const methods = useForm();
  const { handleSubmit } = methods;
  const { create } = useZipCodesHook();
  const [addedList, setAddedList] = useState<object>({});
  const { countryCode, isBelgium } = useCountryConfigHook();

  const onSubmit = (data) => {
    const submitData = {
      ...data,
      is_active: true,
      in_use: true,
      temporary_free_delivery_cost: false,
      not_active_description: "",
      description: "DESCRIPTION",
      country: countryCode,
      order_value: [data.order_value],
      delivery_fee: [data.delivery_fee],
      area: isBelgium ? data.areas?.map((area) => area.value) || [] : undefined,
    };
    create.mutateAsync(submitData).then(() => {
      setAddedList({ ...addedList, [`${selected}`]: submitData });
    });
  };

  return (
    <Modal
      fullHeight
      width={{ _: "90%", md: "60%" }}
      minHeight="50rem"
      onExit={() => setModal("")}
      {...props}
    >
      <ModalTitle mb="2rem" p="0">
        {t("zipcodesAroundYou")}
      </ModalTitle>

      <div>
        <Textfield
          value={radius}
          placeholder="5000 meters"
          label={t("maxDistance")}
          name="radius"
          min={1000}
          step={0.5}
          onChange={(e) => setRadius(parseFloat(e.target.value))}
          type="number"
        />
      </div>

      {radius > 0 && query.isPending ? (
        <div className="mt-[2rem]">
          <Spinner />
        </div>
      ) : query.isPending === false && query.isSuccess ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-[2rem] bg-grey">
              {query?.data?.map((zipcode) => (
                <Row
                  key={zipcode.zipcode}
                  isSelected={selected === zipcode.zipcode}
                  list={addedList}
                  setSelected={setSelected}
                  isPending={create.isPending}
                  {...zipcode}
                />
              ))}
            </div>
          </form>
        </FormProvider>
      ) : null}
    </Modal>
  );
};

/* ----------COMPONENTS-START------------ */

const Row = ({
  distance_meters,
  zipcode,
  isSelected,
  setSelected,
  isPending,
  list,
}) => {
  const { t } = useTranslation("", { keyPrefix: "zip_codes" });

  return (
    <div>
      <div className="flex gap-20 p-8">
        <Checkbox
          checked={isSelected}
          active={isSelected}
          onClick={() =>
            isSelected === false ? setSelected(zipcode) : setSelected("")
          }
        />
        <div className="flex-[0_0_15%] font-semibold">
          {t("zipcode", { zipcode: zipcode })}
        </div>
        <div className="flex-[0_0_30%]">
          {t("distance", { distance: distance_meters })}
        </div>
        {list[zipcode] && (
          <div className="flex-[0_0_10%]">
            <Icon className="h-8 w-8 fill-success" variants="check" />
          </div>
        )}
      </div>
      {isSelected && (
        <Form zipcode={zipcode} isPending={isPending} list={list} />
      )}
    </div>
  );
};

const Form = ({ zipcode, isPending, list }) => {
  const { t } = useTranslation("");
  const formContext = useFormContext();
  const { register, errors, setValue, control, reset } = formContext;
  const { areas } = useAreaHook();
  const {
    address: { zipCodeMaxLength, zipCodePattern },
    isDutch,
  } = useCountryConfigHook();

  useEffect(() => {
    if (list[zipcode]) {
      reset({ ...list[zipcode] });
    } else {
      reset({});
    }
  }, [list]);

  const deliveryTimeOptions = increments((v) => `+${v} ${t("minutes")}`);

  return (
    <div className="flex items-end justify-start gap-4 p-8">
      <Textfield
        name="zipcode_range_begin"
        type="text"
        width="12.5rem"
        label={t("zip_codes.zipcodeStart")}
        maxLength={zipCodeMaxLength}
        ref={register({
          required: {
            value: true,
            message: t("fieldRequired"),
          },
          pattern: {
            value: zipCodePattern,
            message: "Postcode is niet geldig",
          },
        })}
        error={errors?.zipcode_range_begin?.message}
        hideErrorMessage
        defaultValue={`${zipcode}${isDutch ? "AA" : ""}`}
      />
      <Textfield
        name="zipcode_range_end"
        type="text"
        width="12.5rem"
        label={t("zip_codes.zipcodeEnd")}
        maxLength={zipCodeMaxLength}
        ref={register({
          required: {
            value: true,
            message: t("fieldRequired"),
          },
          pattern: {
            value: zipCodePattern,
            message: "Postcode is niet geldig",
          },
        })}
        error={errors?.zipcode_range_end?.message}
        hideErrorMessage
        defaultValue={`${zipcode}${isDutch ? "ZZ" : ""}`}
      />
      <div className="w-60">
        <Controller
          name={`average_delivery_time`}
          control={control}
          defaultValue={0}
          render={(props) => {
            return (
              <Select
                label={t("zip_codes.averageDeliveryTime")}
                onChange={(v) => props.onChange(v.value)}
                value={deliveryTimeOptions?.find(
                  (o) => o.value === props.value
                )}
                options={deliveryTimeOptions}
              />
            );
          }}
        ></Controller>
      </div>
      {isDutch === false && (
        <div className="w-[20rem]">
          <Controller
            name="areas"
            control={control}
            defaultValue={[]}
            render={(props) => {
              return (
                <CreatableSelect
                  label={t("zip_codes.region")}
                  value={props.value}
                  closeMenuOnSelect={false}
                  isMulti
                  name="area"
                  onChange={(v) => props.onChange(v)}
                  options={areas}
                />
              );
            }}
          ></Controller>
        </div>
      )}
      <Textfield
        label={t("zip_codes.minDeliveryAmount")}
        name="order_value"
        step=".01"
        type="number"
        width="12.5rem"
        ref={register({
          required: {
            value: true,
            message: t("fieldRequired"),
          },
        })}
        error={errors?.order_value?.message}
        hideErrorMessage
      />
      <Textfield
        width="12.5rem"
        label={t("zip_codes_create.deliveryFee")}
        name="delivery_fee"
        type="number"
        step=".5"
        ref={register({
          required: {
            value: true,
            message: t("fieldRequired"),
          },
        })}
        error={errors?.delivery_fee?.message}
        hideErrorMessage
      />
      <div>
        <InputColor
          label="Color"
          value={"var(--white)"}
          name="color_code"
          hideTextfield
          showLabel
          onChange={(color) => {
            setValue("color_code", color);
          }}
          ref={register({
            required: {
              value: true,
              message: t("fieldRequired"),
            },
          })}
        />
      </div>
      <div className="ml-auto">
        <Button isPending={isPending} type="submit">
          {t("save")}
        </Button>
      </div>
    </div>
  );
};

/* ----------COMPONENTS-END------------ */

export default FindMyZipcodes;
